import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import styles from "./AuthRouteFallback.module.css"

type ProbeResult =
  | { status: "checking" }
  | { status: "html"; contentType: string }
  | { status: "json"; statusCode: number }
  | { status: "error"; message: string }

// Safety net when /auth/* is accidentally served by the SPA (e.g. broken
// _redirects proxy). Shows a readable screen on mobile instead of a blank page.
export function AuthRouteFallback() {
  const [probe, setProbe] = useState<ProbeResult>({ status: "checking" })

  useEffect(() => {
    let cancelled = false

    async function probeAuthEndpoint() {
      try {
        const res = await fetch("/auth/me", { credentials: "include" })
        const contentType = res.headers.get("content-type") ?? "(unknown)"
        if (contentType.includes("text/html")) {
          if (!cancelled) setProbe({ status: "html", contentType })
          return
        }
        if (!cancelled) setProbe({ status: "json", statusCode: res.status })
      } catch (error) {
        if (!cancelled) {
          setProbe({
            status: "error",
            message: error instanceof Error ? error.message : String(error),
          })
        }
      }
    }

    probeAuthEndpoint()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <main className={styles.root}>
      <h1 className={styles.title}>ログインを開始できませんでした</h1>
      <p className={styles.lead}>
        通常、この画面は表示されず Google
        のログイン画面へ移動します。真っ白な画面やこのメッセージが出る場合、認証サーバーへの接続設定に問題がある可能性があります。
      </p>

      <section className={styles.panel} aria-label="診断情報">
        <h2 className={styles.panelTitle}>診断情報</h2>
        <dl className={styles.dl}>
          <div className={styles.row}>
            <dt>現在のURL</dt>
            <dd>{window.location.href}</dd>
          </div>
          <div className={styles.row}>
            <dt>/auth/me の応答</dt>
            <dd>
              {probe.status === "checking" && "確認中…"}
              {probe.status === "html" &&
                `HTML (${probe.contentType}) — Worker へのプロキシが効いていない可能性があります（Pages Functions の設定を確認）`}
              {probe.status === "json" && `JSON (HTTP ${probe.statusCode})`}
              {probe.status === "error" && `エラー: ${probe.message}`}
            </dd>
          </div>
        </dl>
      </section>

      <div className={styles.actions}>
        <Link to="/" className={styles.primaryButton}>
          トップへ戻る
        </Link>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={() => window.location.reload()}
        >
          再読み込み
        </button>
      </div>
    </main>
  )
}
